/* SSi website – Welsh landing page hero.
 *
 * Mirror of SpanishHero with Welsh-flavoured flourishes:
 *   A. "Greeting overlay"  – red brand background, "Shwmae!" bubble
 *      sat over the hero photo on the right.
 *   B. "Giant word"        – huge typographic "Siarad." (verb, to speak)
 *      sat alongside the proposition copy.
 *
 * Shared scaffolding: same red brand background, same CTAs, same trust
 * copy. Variant chosen via Tweaks panel (cyHeroVariant: 'A' | 'B').
 */
import { Link } from 'react-router-dom';

export function WelshHero({ onStart, t }) {
  const variant = t.cyHeroVariant || 'A';
  return variant === 'B'
    ? <WelshHeroB onStart={onStart} t={t}/>
    : <WelshHeroA onStart={onStart} t={t}/>;
}

/* ──────────────────────────────── A ─ greeting overlay ──────────────────────────────── */
function WelshHeroA({ onStart, t }) {
  return (
    <section className="ssi-cyHero ssi-cyHero--A" style={cyHeroStyles.wrapA}>
      <div className="ssi-cyHero__leftA" style={cyHeroStyles.leftA}>
        {t.cyHeroKicker && <div style={cyHeroStyles.kicker}>{t.cyHeroKicker}</div>}
        <h1 style={cyHeroStyles.h1}>{t.cyHeroTitle}</h1>
        <p style={cyHeroStyles.lede}>{t.cyHeroSub1}</p>
        <p style={cyHeroStyles.lede}>{t.cyHeroSub2}</p>
        <div style={cyHeroStyles.ctas}>
          <Link to="/pay?course=en-cy-south" className="btn btn--onDark">{t.cyHeroCta1}</Link>
          <a href="#sample" className="btn" style={cyHeroStyles.ghost}>{t.cyHeroCta2}</a>
        </div>
        <p style={cyHeroStyles.trust}>{t.cyHeroTrust}</p>
      </div>
      <div className="ssi-cyHero__rightA" style={cyHeroStyles.rightA}>
        <img src="/assets/hero_couple_laughing.png?v=6" alt="" style={cyHeroStyles.heroImg}/>
        <div className="ssi-cyHero__shwmae" style={cyHeroStyles.shwmaeBubble} aria-hidden>
          <span style={cyHeroStyles.shwmaeWord}>{t.cyHeroBubble || 'Shwmae!'}</span>
          <span style={cyHeroStyles.shwmaeTail}/>
        </div>
      </div>
    </section>
  );
}

/* ──────────────────────────────── B ─ giant word ──────────────────────────────── */
function WelshHeroB({ onStart, t }) {
  return (
    <section className="ssi-cyHero ssi-cyHero--B" style={cyHeroStyles.wrapB}>
      <div className="ssi-cyHero__innerB" style={cyHeroStyles.innerB}>
        <div className="ssi-cyHero__giantWrap" style={cyHeroStyles.giantWrap}>
          <span style={cyHeroStyles.giantTranslate}>(verb) to speak</span>
          <div style={cyHeroStyles.giant} aria-hidden>{t.cyHeroGiantWord}</div>
          <div style={cyHeroStyles.giantUnderline}/>
        </div>
        <div style={cyHeroStyles.copyB}>
          {t.cyHeroKicker && <div style={cyHeroStyles.kickerB}>{t.cyHeroKicker}</div>}
          <h1 style={cyHeroStyles.h1B}>{t.cyHeroTitle}</h1>
          <p style={cyHeroStyles.ledeB}>{t.cyHeroSub1}</p>
          <p style={cyHeroStyles.ledeB}>{t.cyHeroSub2}</p>
          <div style={cyHeroStyles.ctas}>
            <Link to="/pay?course=en-cy-south" className="btn btn--onDark">{t.cyHeroCta1}</Link>
            <a href="#sample" className="btn" style={cyHeroStyles.ghost}>{t.cyHeroCta2}</a>
          </div>
          <p style={cyHeroStyles.trustB}>{t.cyHeroTrust}</p>
        </div>
      </div>
    </section>
  );
}

const cyHeroStyles = {
  /* ─── shared ─── */
  kicker: {
    fontSize: 12, letterSpacing: '.18em', textTransform: 'uppercase',
    color: '#FFD8D4', fontWeight: 600, marginBottom: 18,
  },
  h1: {
    fontFamily: 'var(--font-display)', fontSize: 64, lineHeight: 1.04,
    color: '#fff', letterSpacing: '-.015em', margin: '0 0 20px', textWrap: 'balance',
  },
  lede: { fontSize: 18, lineHeight: 1.55, color: 'rgba(255,255,255,.92)', margin: '0 0 18px', maxWidth: 540 },
  ctas: { display: 'flex', gap: 12, marginTop: 20, marginBottom: 24 },
  ghost: { background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,.5)' },
  trust: { fontSize: 13, color: 'rgba(255,255,255,.72)', margin: 0 },

  /* ─── variant A: greeting overlay ─── */
  wrapA: {
    background: 'var(--brandRed)', color: '#fff',
    padding: '88px 0 88px',
    position: 'relative', overflow: 'hidden',
    minHeight: 560,
  },
  leftA: { maxWidth: 600, padding: '0 32px', position: 'relative', zIndex: 2 },
  rightA: {
    position: 'absolute', right: 0, top: 0, bottom: 0,
    width: '50%',
    display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
  },
  heroImg: { width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center bottom' },
  shwmaeBubble: {
    position: 'absolute',
    left: 'calc(8% )', top: 64,
    background: '#FFD84D',
    color: '#490300',
    padding: '18px 28px 22px',
    borderRadius: 18,
    transform: 'rotate(-5deg)',
    boxShadow: '0 18px 40px -14px rgba(0,0,0,.35), 0 2px 0 rgba(0,0,0,.08)',
    zIndex: 3,
  },
  shwmaeWord: {
    fontFamily: 'var(--font-display)',
    fontWeight: 700,
    fontSize: 48,
    lineHeight: 1,
    letterSpacing: '-.02em',
    display: 'inline-block',
    fontStyle: 'italic',
  },
  shwmaeTail: {
    position: 'absolute',
    left: 36, bottom: -12,
    width: 22, height: 22,
    background: '#FFD84D',
    borderBottomLeftRadius: 4,
    borderStyle: 'solid',
    borderWidth: '0 0 2px 2px',
    borderColor: 'transparent',
    transform: 'rotate(-45deg)',
  },

  /* ─── variant B: giant word ─── */
  wrapB: {
    background: 'var(--brandRed)', color: '#fff',
    padding: '80px 0 88px',
    position: 'relative', overflow: 'hidden',
  },
  innerB: {
    maxWidth: 1240, margin: '0 auto', padding: '0 32px',
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 56,
    alignItems: 'center',
  },
  giantWrap: { position: 'relative' },
  giantTranslate: {
    display: 'inline-block',
    fontSize: 13,
    color: 'rgba(255,255,255,.7)',
    letterSpacing: '.02em',
    fontStyle: 'italic',
    marginBottom: 4,
    marginLeft: 4,
  },
  giant: {
    fontFamily: 'var(--font-display)',
    fontSize: 'clamp(120px, 15vw, 220px)',
    lineHeight: .95,
    fontWeight: 700,
    letterSpacing: '-.03em',
    color: '#fff',
    margin: 0,
  },
  giantUnderline: {
    width: 80, height: 4,
    background: '#FFD84D',
    borderRadius: 4,
    marginTop: 20,
    marginLeft: 4,
  },
  copyB: { maxWidth: 520 },
  kickerB: {
    fontSize: 12, letterSpacing: '.18em', textTransform: 'uppercase',
    color: '#FFD8D4', fontWeight: 600, marginBottom: 16,
  },
  h1B: {
    fontFamily: 'var(--font-display)', fontSize: 44, lineHeight: 1.1,
    color: '#fff', letterSpacing: '-.015em', margin: '0 0 18px', textWrap: 'balance',
  },
  ledeB: {
    fontSize: 16.5, lineHeight: 1.55, color: 'rgba(255,255,255,.92)',
    margin: '0 0 16px', maxWidth: 520,
  },
  trustB: { fontSize: 13, color: 'rgba(255,255,255,.72)', margin: 0 },
};
